import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Copy, Check, FileText, Calendar, Hash, ClipboardList } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const ResultCard = ({ result, onCopy, copied }) => {
  const { t, language } = useLanguage();
  const isValid = result?.valid;

  if (!result) return null;

  const formatKenmerkDisplay = (kenmerk) => {
    if (!kenmerk) return '';
    const clean = kenmerk.replace(/\s/g, '');
    // Check if it contains letters (aanslagnummer)
    if (/[A-Za-z]/.test(clean)) {
      return clean; // Don't format aanslagnummers with spaces
    }
    return clean.replace(/(\d{4})/g, '$1 ').trim();
  };

  const getInputTypeLabel = () => {
    if (result.input_type === 'aanslagnummer') {
      return language === 'nl' ? 'Aanslagnummer' : 'Assessment Number';
    }
    return language === 'nl' ? 'Betalingskenmerk' : 'Payment Reference';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        relative bg-white dark:bg-slate-900 rounded-xl border 
        ${isValid 
          ? 'border-l-4 border-l-success border-slate-200 dark:border-slate-700' 
          : 'border-l-4 border-l-error border-slate-200 dark:border-slate-700'}
        p-6 shadow-sm hover:shadow-md transition-shadow
      `}
      data-testid="result-card"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {isValid ? (
            <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-error" />
            </div>
          )}
          <div>
            <h3 className="font-heading font-semibold text-slate-900 dark:text-white">
              {isValid ? t('result_valid') : t('result_invalid')}
            </h3>
            <div className="flex items-center gap-2">
              <p className="font-mono text-lg tracking-wider text-slate-500 dark:text-slate-400">
                {formatKenmerkDisplay(result.kenmerk)}
              </p>
              {isValid && result.input_type && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                  {getInputTypeLabel()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Copy button */}
        <button
          onClick={onCopy}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 border-accent/20 bg-accent/5 text-accent hover:bg-accent/10 hover:border-accent/40 dark:border-accent/30 dark:bg-accent/10 dark:hover:bg-accent/20 transition-all"
          data-testid="copy-result-btn"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-success" />
              <span className="text-success">{t('result_copied')}</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>{t('result_copy')}</span>
            </>
          )}
        </button>
      </div>

      {/* Error message */}
      {!isValid && result.error && (
        <div className="p-4 bg-error/5 rounded-lg border border-error/20 mb-4">
          <p className="text-error text-sm">{result.error}</p>
        </div>
      )}

      {/* Result details */}
      {isValid && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Tax type */}
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <FileText className="w-5 h-5 text-accent mt-0.5" />
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{t('result_tax_type')}</p>
              <p className="font-medium text-slate-900 dark:text-white">
                {result.belastingtype?.[language] || result.belastingtype?.nl}
              </p>
            </div>
          </div>

          {/* Year */}
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <Calendar className="w-5 h-5 text-accent mt-0.5" />
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{t('result_year')}</p>
              <p className="font-medium text-slate-900 dark:text-white">{result.jaar}</p>
            </div>
          </div>

          {/* Period */}
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <Hash className="w-5 h-5 text-accent mt-0.5" />
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{t('result_period')}</p>
              <p className="font-medium text-slate-900 dark:text-white">{result.tijdvak}</p>
              <p className="text-xs text-slate-400">
                {result.tijdvak_type?.[language] || result.tijdvak_type?.nl}
              </p>
            </div>
          </div>

          {/* Assessment */}
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <ClipboardList className="w-5 h-5 text-accent mt-0.5" />
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{t('result_assessment')}</p>
              <p className="font-mono font-medium text-slate-900 dark:text-white">{result.aanslagnummer}</p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};
